import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material'
import {
  CloudUpload,
  ContentPaste,
  Visibility,
  VisibilityOff,
  Schedule,
  Lock,
  Clear,
  Login as LoginIcon,
} from '@mui/icons-material'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

interface UploadProgress {
  loaded: number
  total: number
}

interface FileUploadResponse {
  status: 'success' | 'error'
  message?: string
  files?: Array<{
    id: string
    filename: string
    status: string
    error?: string
  }>
}

interface PasteResponse {
  id: string
  status?: string
}

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

export default function Home() {
  const { isAuthenticated, credentials } = useAuth()
  const navigate = useNavigate()
  const [tabValue, setTabValue] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [text, setText] = useState('')
  const [isEncrypted, setIsEncrypted] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [expiryTime, setExpiryTime] = useState('1d')
  const [error, setError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filePassword, setFilePassword] = useState('')
  const [fileExpiryTime, setFileExpiryTime] = useState('1d')
  const [isFileEncrypted, setIsFileEncrypted] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setError(null)
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `文件大小超过限制 (${formatFileSize(MAX_FILE_SIZE)})`
    }
    return null
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const error = validateFile(file)
    if (error) {
      setError(error)
      return
    }

    setSelectedFile(file)
    setError(null)
  }

  const handleClearFile = () => {
    setSelectedFile(null)
    setError(null)
    setUploadProgress(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileUpload = async () => {
    if (!selectedFile) return

    // 检查文件大小
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`文件大小超过限制 (${formatFileSize(MAX_FILE_SIZE)})`)
      return
    }

    setUploading(true)
    setError(null)
    try {
      // 创建 FormData 对象
      const formData = new FormData()
      formData.append('files[]', selectedFile)
      formData.append('expiresIn', fileExpiryTime)
      
      if (isFileEncrypted && filePassword) {
        formData.append('password', filePassword)
      }

      formData.append('maxViews', '0')

      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress({
            loaded: event.loaded,
            total: event.total,
          })
        }
      }

      const response = await new Promise<FileUploadResponse>((resolve, reject) => {
        xhr.open('POST', '/api/file')
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText)
              if (data.status === 'error') {
                reject(new Error(data.message || '上传失败'))
                return
              }
              resolve(data)
            } catch {
              reject(new Error('上传失败：服务器响应格式错误'))
            }
          } else {
            reject(new Error(xhr.status === 401 ? '未授权访问' : `上传失败：${xhr.statusText}`))
          }
        }

        xhr.onerror = () => reject(new Error('上传失败：网络错误'))
        xhr.send(formData)
      })

      if (response.status === 'success' && response.files?.[0]?.id) {
        window.location.href = `/share/file/${response.files[0].id}`
      } else {
        throw new Error('上传失败：无效的响应格式')
      }
    } catch (error) {
      console.error('上传错误:', error)
      setError(error instanceof Error ? error.message : '上传失败，请重试')
    } finally {
      setUploading(false)
      setUploadProgress(null)
    }
  }

  const handleTextShare = async () => {
    if (!text.trim()) {
      setError('请输入要分享的文本')
      return
    }

    if (isEncrypted && !password.trim()) {
      setError('请输入加密密码')
      return
    }

    setUploading(true)
    setError(null)
    try {
      const response = await fetch('/api/paste', {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
          'Authorization': `Basic ${credentials}`,
        },
        body: text,
      })

      if (!response.ok) {
        throw new Error('分享失败')
      }

      const data = await response.json() as PasteResponse
      
      // 如果有加密，需要设置密码
      if (isEncrypted && password) {
        const encryptResponse = await fetch(`/api/paste/${data.id}/encrypt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${credentials}`,
          },
          body: JSON.stringify({
            password,
            expiryTime,
          }),
        })

        if (!encryptResponse.ok) {
          throw new Error('加密设置失败')
        }
      }

      window.location.href = `/share/paste/${data.id}`
    } catch (error) {
      console.error('分享错误:', error)
      setError(error instanceof Error ? error.message : '分享失败，请重试')
    } finally {
      setUploading(false)
    }
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const renderFileUploadContent = () => {
    if (selectedFile) {
      return (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <Typography variant="subtitle1" gutterBottom>
            已选择：{selectedFile.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            文件大小：{formatFileSize(selectedFile.size)}
          </Typography>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>
                <Schedule sx={{ mr: 1 }} />
                过期时间
              </InputLabel>
              <Select
                value={fileExpiryTime}
                label="过期时间"
                onChange={(e) => setFileExpiryTime(e.target.value)}
                disabled={uploading}
              >
                <MenuItem value="1h">1小时</MenuItem>
                <MenuItem value="1d">1天</MenuItem>
                <MenuItem value="7d">7天</MenuItem>
                <MenuItem value="30d">30天</MenuItem>
                <MenuItem value="never">永不过期</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={isFileEncrypted}
                  onChange={(e) => setIsFileEncrypted(e.target.checked)}
                  disabled={uploading}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Lock sx={{ mr: 1 }} />
                  加密保护
                </Box>
              }
            />
          </Box>

          {isFileEncrypted && (
            <TextField
              sx={{ mt: 2 }}
              type={showPassword ? 'text' : 'password'}
              label="访问密码"
              value={filePassword}
              onChange={(e) => setFilePassword(e.target.value)}
              disabled={uploading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {uploadProgress && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(uploadProgress.loaded / uploadProgress.total) * 100}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                {formatFileSize(uploadProgress.loaded)} / {formatFileSize(uploadProgress.total)}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleFileUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {uploading ? '上传中...' : '开始上传'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFile}
              disabled={uploading}
              startIcon={<Clear />}
            >
              清除
            </Button>
          </Box>
        </Box>
      )
    }

    return (
      <>
        <input
          accept="*/*"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          onChange={handleFileSelect}
          disabled={uploading}
          ref={fileInputRef}
        />
        <label htmlFor="file-upload">
          <Button
            variant="contained"
            component="span"
            disabled={uploading}
            startIcon={<CloudUpload />}
          >
            选择文件
          </Button>
        </label>
        <Typography variant="caption" sx={{ mt: 2 }} color="text.secondary">
          支持所有文件类型，单个文件最大 {formatFileSize(MAX_FILE_SIZE)}
        </Typography>
      </>
    )
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          CloudPaste - 文件分享平台
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
          简单、快速、安全的文件分享服务
        </Typography>
      </Box>

      {isAuthenticated ? (
        <Paper sx={{ width: '100%', mt: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab icon={<CloudUpload />} label="文件上传" />
            <Tab icon={<ContentPaste />} label="文本分享" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 3,
              }}
            >
              {renderFileUploadContent()}
              {error && (
                <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                  {error}
                </Alert>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="在此输入要分享的文本..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={uploading}
              />
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>
                    <Schedule sx={{ mr: 1 }} />
                    过期时间
                  </InputLabel>
                  <Select
                    value={expiryTime}
                    label="过期时间"
                    onChange={(e) => setExpiryTime(e.target.value)}
                    disabled={uploading}
                  >
                    <MenuItem value="1h">1小时</MenuItem>
                    <MenuItem value="1d">1天</MenuItem>
                    <MenuItem value="7d">7天</MenuItem>
                    <MenuItem value="30d">30天</MenuItem>
                    <MenuItem value="never">永不过期</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isEncrypted}
                      onChange={(e) => setIsEncrypted(e.target.checked)}
                      disabled={uploading}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Lock sx={{ mr: 1 }} />
                      加密保护
                    </Box>
                  }
                />
              </Box>

              {isEncrypted && (
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  type={showPassword ? 'text' : 'password'}
                  label="访问密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={uploading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleTextShare}
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={20} /> : <ContentPaste />}
                >
                  {uploading ? '分享中...' : '分享文本'}
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            欢迎使用 CloudPaste 文件分享平台
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            这是一个简单、快速、安全的文件分享服务。您可以上传文件或分享文本，支持加密保护和自定义过期时间。
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            startIcon={<LoginIcon />}
            size="large"
            sx={{ mt: 2 }}
          >
            立即登录使用
          </Button>
        </Paper>
      )}
    </Container>
  )
} 